import React, { useCallback } from 'react'
import { cn } from 'utils/cn'
import { sanitizeNumericInput } from 'utils/search-filter-validation'

interface FilterInputProps {
    placeholder?: string
    value: string
    onChange: (value: string) => void
    borderRadius?: string
    width?: string
    disabled?: boolean
    type?: string
    paddingRight?: string
    startDecorator?: React.ReactNode
    isConnected?: boolean
    isFirstInGroup?: boolean
    isLastInGroup?: boolean
    style?: React.CSSProperties
    minValue?: number
    maxValue?: number
}

export const FilterInput: React.FC<FilterInputProps> = ({
    placeholder,
    value,
    onChange,
    width = '80px',
    disabled = false,
    type = 'text',
    startDecorator,
    isConnected = false,
    isFirstInGroup = false,
    isLastInGroup = false,
    style,
    minValue,
    maxValue,
}) => {
    const isNumeric = type === 'number'

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value
            if (isNumeric) {
                const sanitized = sanitizeNumericInput(raw, { min: minValue, max: maxValue })
                if (sanitized === null) return
                onChange(sanitized)
                return
            }
            onChange(raw)
        },
        [onChange, isNumeric, minValue, maxValue]
    )

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault()
        }
    }, [])

    const borderClasses = cn(
        isConnected && isFirstInGroup && 'rounded-r-none border-r-0',
        isConnected && isLastInGroup && 'rounded-l-none',
        isConnected && !isFirstInGroup && !isLastInGroup && 'rounded-none border-r-0',
        !isConnected && 'rounded-md'
    )

    return (
        <div
            className={cn('relative flex items-center', isConnected && !isFirstInGroup && '-ml-px')}
            style={{ width, minWidth: width, maxWidth: width, ...style }}
        >
            {startDecorator && <div className="flex items-center pl-1.5 absolute left-0 z-10">{startDecorator}</div>}
            <input
                type="text"
                inputMode={isNumeric ? 'numeric' : undefined}
                placeholder={placeholder}
                value={value}
                disabled={disabled}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className={cn(
                    'w-full h-8 text-[12px] font-medium font-[Inter,sans-serif]',
                    'border border-[var(--lh-border)]',
                    'bg-[var(--lh-surface)]',
                    'text-[var(--lh-fg)]',
                    'px-2.5 outline-none',
                    'focus:border-[var(--lh-accent)]',
                    'placeholder:text-[var(--lh-fg-muted)] placeholder:font-normal',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors',
                    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                    borderClasses,
                    startDecorator && 'pl-7'
                )}
            />
        </div>
    )
}
